import { Cart, CartUpdate, CartUpdateAction } from "@commercetools/platform-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ProductConfiguration } from "@wttb/product-configurator";
import { Session } from "next-auth";
import { getSession, signIn, useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";

import { updateCartById } from "@/app/actions";
import { getProductDataBySKU } from "@/clients/product-config/product";
import {
  mapCustomLineItemPayload,
  updateTOSessionWithPromotions as updateTalonSessionWithPromotions,
} from "@/clients/talon-one/cart";
import { getProductConfigSummary } from "@/features/product/components/hooks/use-product-config-summary";
import { getUpdateArtworkCheckActionPayload } from "./use-cart-addon";

export interface CartItemsUpdate {
  cartVersion: number;
  cartId: string;
  items: {
    sku: string;
    lineItemId: string;
    deliveryRuleId?: string;
    config?: ProductConfiguration;
    artworkCheckPrice?: number;
  }[];
  cart?: Cart | null;
}

export interface CustomCartItemsUpdate {
  cartVersion: number;
  cartId: string;
  items: {
    customLineItemId: string;
    printPerksUsedAmount: number;
  }[];
  cart?: Cart | null;
}

async function updateCartItems(
  session: Session | null,
  input: CartItemsUpdate,
  t: ReturnType<typeof useTranslations<string>>,
) {
  if (!session) {
    await signIn("guest", { redirect: false });
    session = await getSession();
  }

  const actionsPromises = input.items.map(async (item) => {
    const res: CartUpdateAction[] = [];
    const lineItem = input.cart?.lineItems?.find((lineItem) => lineItem.id === item.lineItemId);

    if (item.config) {
      const versions = +(item.config.versions ?? 0);

      // total qty = item qty * versions
      let quantity = +(item?.config?.quantity ?? 0);
      if (versions) quantity *= versions;

      const attributes = (await getProductDataBySKU(item.sku!)).attributes;
      res.push(
        ...[
          {
            action: "setLineItemCustomField" as const,
            lineItemId: item.lineItemId,
            name: "productConfig",
            value: JSON.stringify(item.config),
          },
          {
            action: "setLineItemCustomField" as const,
            lineItemId: item.lineItemId,
            name: "productConfigDescription",
            value: JSON.stringify(getProductConfigSummary({ config: item.config, attributes, t })),
          },
        ],
      );

      const currentLineItem = input.cart?.lineItems.find((i) => i.id === item.lineItemId);
      if (quantity !== currentLineItem?.quantity) {
        res.push({
          action: "changeLineItemQuantity" as const,
          lineItemId: item.lineItemId,
          quantity,
        });
      }
    }

    if (typeof item.deliveryRuleId !== "undefined") {
      res.push({
        action: "setLineItemCustomField" as const,
        lineItemId: item.lineItemId,
        name: "deliveryRuleId",
        value: item.deliveryRuleId,
      });
    }
    const artworkCheckUpdatePayload = getUpdateArtworkCheckActionPayload(
      input?.cart,
      !!item.artworkCheckPrice,
      lineItem?.key ?? "",
      item.artworkCheckPrice ?? 0,
    );

    if (artworkCheckUpdatePayload) {
      res.push(await mapCustomLineItemPayload(artworkCheckUpdatePayload));
    }

    return res;
  });

  const actions = (await Promise.all(actionsPromises)).flat();

  const body: CartUpdate = {
    version: input.cartVersion,
    actions,
  };

  let cart = await updateCartById(input.cartId, body);
  const response = await updateTalonSessionWithPromotions({ cart });
  if (response?.cart) {
    cart = response.cart;
  }
  return cart;
}

async function updateCustomCartItems(session: Session | null, input: CustomCartItemsUpdate) {
  if (!session) {
    await signIn("guest", { redirect: false });
    session = await getSession();
  }

  const actionsPromises = input.items.map((item) => {
    const res: CartUpdateAction[] = [];

    res.push({
      action: "setCustomLineItemCustomField" as const,
      customLineItemId: item.customLineItemId,
      name: "printPerksUsedAmount",
      value: item.printPerksUsedAmount,
    });

    return res;
  });

  const actions = (await Promise.all(actionsPromises)).flat();

  const body: CartUpdate = {
    version: input.cartVersion,
    actions,
  };

  return updateCartById(input.cartId, body);
}

export function useUpdateCartItemsMutation() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const t = useTranslations("common");
  return useMutation((input: CartItemsUpdate) => updateCartItems(session, input, t), {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["CART"] });
    },
    onError: (data: Error) => {
      toast.error("Error updating cart item: " + data.message);
    },
  });
}

export function useUpdateCartCustomItemsMutation() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  return useMutation((input: CustomCartItemsUpdate) => updateCustomCartItems(session, input), {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["CART"] });
    },
    onError: (data: Error) => {
      toast.error("Error updating cart item: " + data.message);
    },
  });
}
